// ---------------------------------------------------------------
// MOCK DATA — mirrors the future MongoDB structure (see contracts.md)
// All of this becomes editable from the /admin panel in Phase 2.
// ---------------------------------------------------------------

export const siteContent = {
  brand: {
    namePrimary: "DAVIDDOESAI",
    nameScript: "studio",
    copyright: "© 2025 - 2026 DavidDoesAI Studio",
  },
  hero: {
    title: "DAVIDDOESAI",
    subtitle: "an AI Video Production Studio",
    cutSeconds: 2,
    lastCutSeconds: 3,
    videos: [
      "/videos/hero-1.mp4",
      "/videos/hero-2.mp4",
      "/videos/hero-3.mp4",
      "/videos/hero-4.mp4",
    ],
  },
  home: {
    intro:
      "DavidDoesAI Studio is a creative AI video production studio led by editor and creative director David Das. With more than 200 projects delivered and over 150 million organic views generated, we blend hands-on editing craft with the latest generative AI models to tell stories that move people — keeping human creativity at the heart of every frame.",
    sampleWorkTitle: "Sample Work",
    teamStatement:
      "From viral reels and UGC ads to cinematic brand films, our studio pairs artistic direction with deep technical mastery of tools like Google Veo, Kling and Seedance — helping more than 40 brands connect with audiences around the world.",
    collageImage:
      "https://images.pexels.com/photos/8088386/pexels-photo-8088386.jpeg",
    ctaTitle: "Let's Create. Together.",
    ctaButton: "Contact Us",
    portfolioButton: "View Our Portfolio",
  },
  portfolio: {
    heading: "Portfolio",
    intro:
      "A selection of AI-crafted commercials, brand spec ads and original concepts — plus a look at a few projects currently in development.",
    sectionMain: "Our Portfolio",
    sectionVerticals: "Verticals",
    sectionFun: "And Just For Fun…",
  },
  about: {
    heading: "About",
    intro:
      "Human creativity first. AI as the amplifier. That's how we make brands impossible to ignore.",
    storyTitle: "The Studio",
    story1:
      "DavidDoesAI Studio was born from a simple belief: generative AI is the most exciting creative tool since the camera itself. Founded by David Das — an AI video editor and creative director with 200+ delivered projects — the studio crafts everything from faceless YouTube storytelling to high-converting UGC ads and cinematic brand spec work.",
    story2:
      "Every project starts with story, emotion and craft. Then we reach for the right model — Google Veo, Kling, Seedance, Sora and 20+ more — to bring it to life faster, bolder and at a scale traditional production simply can't match.",
    statsTitle: "By The Numbers",
    stats: [
      { value: "150M+", label: "Organic views generated" },
      { value: "40+", label: "Brands transformed" },
      { value: "12x", label: "Avg. engagement lift" },
      { value: "72hrs", label: "Concept to viral-ready" },
    ],
    servicesTitle: "What We Do",
    services: [
      { name: "AI Video Editing", desc: "Professional storytelling videos for YouTube channels, crafted with Google Veo 3 and Higgsfield." },
      { name: "AI Reels & Stories", desc: "Eye-catching short-form content optimized for Instagram, TikTok and YouTube Shorts." },
      { name: "UGC Ads with AI", desc: "Authentic user-generated style ads built with HeyGen and ElevenLabs for maximum conversions." },
      { name: "AI Meta Ads", desc: "High-converting Facebook and Instagram ad creatives designed and optimized with AI." },
      { name: "Script Writing", desc: "Custom scripts tailored to your vision — hooks, retention and CTAs that keep people watching." },
      { name: "AI Tools Consultation", desc: "Expert guidance on leveraging Freepik, ImagineArt and 20+ AI tools in your workflow." },
    ],
    toolsTitle: "Models We Master",
    tools: [
      "Kling 3.0", "Google Veo", "OpenAI Sora", "Seedance 2.0", "Runway",
      "MiniMax Hailuo", "PixVerse", "Luma", "Flux", "Google Imagen",
      "Higgsfield", "HeyGen", "ElevenLabs", "Magnific AI", "Krea", "Recraft",
    ],
    studioImage:
      "https://images.unsplash.com/photo-1569620978580-980c2592fe9b",
  },
  contact: {
    heading: "Contact Us",
    intro:
      "Have a project in mind, need creative direction, or simply curious about AI video production? Reach out and say hi — we reply within 24 hours.",
    formNote: "Let us know how we can help.",
    email: "david@daviddoesai.me",
    calendly: "https://calendly.com/davidddoes_ai/30min",
  },
  footer: {
    newsletterTitle: "Stay in the loop",
    newsletterText:
      "Sign up for occasional updates on new work, the AI tools we love, and studio news. We respect your privacy and will never share your information with any third-party vendors.",
    newsletterButton: "Keep in touch",
  },
  trust: {
    enabled: false,
    kicker: "Trusted By",
    title: "40+ brands worldwide",
    brands: ["Lumen", "Aurora", "Atlas Motors", "Solstice", "Nova", "Wildlands", "Meridian", "Pixelworks"],
    testimonials: [
      {
        quote: "The turnaround was unreal - a full campaign of AI spec ads in under a week, and every cut still felt hand-crafted.",
        author: "Placeholder Client",
        role: "Marketing Director",
      },
      {
        quote: "Our reels tripled their reach within a month. That blend of AI speed and real editorial taste is rare.",
        author: "Placeholder Client",
        role: "Founder, D2C Brand",
      },
      {
        quote: "It looked like a six-figure production. It wasn't. That's the magic of this studio.",
        author: "Placeholder Client",
        role: "Creative Lead",
      },
    ],
  },
  socials: [
    { name: "Instagram", url: "https://www.instagram.com/daviddoes_ai/" },
    { name: "YouTube", url: "https://www.youtube.com/channel/UCYeKbRNI7v9rrpxN71__E1A" },
    { name: "LinkedIn", url: "https://www.linkedin.com/in/daviddasofficial/" },
    { name: "Upwork", url: "https://www.upwork.com/freelancers/daviddasofficial" },
    { name: "Fiverr", url: "https://www.fiverr.com/daviddas531?public_mode=true" },
  ],
};

// section: "portfolio" | "verticals" | "fun"  ·  featured => shown on homepage
export const videos = [
  {
    id: "v1",
    title: "Our AI Showreel (2025)",
    thumbnail: "https://images.unsplash.com/photo-1758426637672-936d5ffecf5e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    videoUrl: "/videos/hero-1.mp4",
    section: "portfolio",
    featured: true,
    order: 1,
  },
  {
    id: "v2",
    title: "Neon Horizon — Tech Spec Ad",
    thumbnail: "https://images.pexels.com/photos/8566536/pexels-photo-8566536.jpeg?w=1200",
    videoUrl: "/videos/clip-sintel.mp4",
    section: "portfolio",
    featured: true,
    order: 2,
  },
  {
    id: "v3",
    title: "Aurora Watches Spec Ad",
    thumbnail: "https://images.unsplash.com/photo-1781444292792-b3a70230e947?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    videoUrl: "/videos/hero-2.mp4",
    section: "portfolio",
    featured: true,
    order: 3,
  },
  {
    id: "v4",
    title: "Wildlands Conservation Spec Ad",
    thumbnail: "https://images.unsplash.com/photo-1470164971321-eb5ac2c35f2e?w=1200&q=85",
    videoUrl: "/videos/clip-jellyfish.mp4",
    section: "portfolio",
    featured: true,
    order: 4,
  },
  {
    id: "v5",
    title: "Solstice Travel Spec Ad",
    thumbnail: "https://images.unsplash.com/photo-1422466654108-5e533f591881?w=1200&q=85",
    videoUrl: "/videos/hero-3.mp4",
    section: "portfolio",
    featured: false,
    order: 5,
  },
  {
    id: "v6",
    title: "Atlas Motors Spec Ad",
    thumbnail: "https://images.unsplash.com/photo-1466133633688-187f0b492390?w=1200&q=85",
    videoUrl: "/videos/hero-3.mp4",
    section: "portfolio",
    featured: false,
    order: 6,
  },
  {
    id: "v7",
    title: "Lumen HR Platform Spec Ad",
    thumbnail: "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=1200&q=85",
    videoUrl: "/videos/hero-3.mp4",
    section: "portfolio",
    featured: false,
    order: 7,
  },
  {
    id: "v8",
    title: "Nova Skincare Spec Ad (coming soon!)",
    thumbnail: "https://images.unsplash.com/photo-1781312862732-7e95d1f80ed6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    videoUrl: "/videos/hero-4.mp4",
    section: "portfolio",
    featured: false,
    order: 8,
  },
  {
    id: "v9",
    title: "Social Announcement",
    thumbnail: "https://images.pexels.com/photos/15649980/pexels-photo-15649980.jpeg?w=800",
    videoUrl: "/videos/hero-1.mp4",
    section: "verticals",
    featured: false,
    order: 9,
  },
  {
    id: "v10",
    title: "BTS: The Making of Neon Horizon (coming soon!)",
    thumbnail: "https://images.pexels.com/photos/29433729/pexels-photo-29433729.png?w=800",
    videoUrl: "/videos/hero-2.mp4",
    section: "verticals",
    featured: false,
    order: 10,
  },
  {
    id: "v11",
    title: "Midnight City Series — Episode 1",
    thumbnail: "https://images.pexels.com/photos/11916973/pexels-photo-11916973.jpeg?w=800",
    videoUrl: "/videos/clip-sintel.mp4",
    section: "verticals",
    featured: false,
    order: 11,
  },
  {
    id: "v12",
    title: "AI Bloopers Reel",
    thumbnail: "https://images.pexels.com/photos/8566521/pexels-photo-8566521.jpeg?w=1200",
    videoUrl: "/videos/clip-bunny.mp4",
    section: "fun",
    featured: false,
    order: 12,
  },
  {
    id: "v13",
    title: "Pixel Valley Short",
    thumbnail: "https://images.pexels.com/photos/10480641/pexels-photo-10480641.jpeg?w=1200",
    videoUrl: "/videos/clip-jellyfish.mp4",
    section: "fun",
    featured: false,
    order: 13,
  },
];
